import { useState } from 'react'
import type {
  LiveEventAdmin,
  LiveEventGameConfig,
  LiveEventInput,
} from 'api/generated'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Checkbox } from 'components/ui/checkbox'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Switch } from 'components/ui/switch'
import { Textarea } from 'components/ui/textarea'
import {
  DEFAULT_THEME,
  fromDateTimeLocal,
  toDateTimeLocal,
} from 'helpers/live-events'
import { Loader } from 'lucide-react'

type ColorKey = keyof typeof DEFAULT_THEME
type ConfigKey = keyof LiveEventGameConfig

const COLOR_FIELDS: { key: ColorKey; label: string; hint: string }[] = [
  {
    key: 'primary_color',
    label: 'Colore principale',
    hint: 'Pulsanti, evidenze, racchetta',
  },
  {
    key: 'secondary_color',
    label: 'Colore del campo',
    hint: 'Campo e superfici scure',
  },
  { key: 'accent_color', label: 'Colore accento', hint: 'Bersagli sul muro' },
]

const CONFIG_FIELDS: { key: ConfigKey; label: string; step: string }[] = [
  { key: 'lives', label: 'Vite', step: '1' },
  { key: 'base_speed', label: 'Velocità iniziale (×)', step: '0.05' },
  { key: 'speed_step', label: 'Aumento per colpo (0.05 = +5%)', step: '0.01' },
  { key: 'max_speed', label: 'Velocità massima (×)', step: '0.1' },
  { key: 'racket_width', label: 'Racchetta iniziale (frazione)', step: '0.01' },
  {
    key: 'min_racket_width',
    label: 'Racchetta minima (frazione)',
    step: '0.01',
  },
  { key: 'target_every_hits', label: 'Bersaglio ogni N colpi', step: '1' },
  { key: 'target_points', label: 'Punti bersaglio', step: '1' },
]

const HEX = /^#[0-9a-f]{6}$/i

const randomKey = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map((b) => b.toString(36).padStart(2, '0'))
    .join('')
    .slice(0, 10)

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const stringValues = (source: object | null | undefined) =>
  Object.fromEntries(
    Object.entries(source ?? {}).map(([k, v]) => [
      k,
      v == null ? '' : String(v),
    ])
  ) as Record<string, string>

/** A tiny top view of the game with the chosen colours, as the app draws it. */
const CourtPreview = ({
  colors,
  logo,
}: {
  colors: Record<ColorKey, string>
  logo: string | null
}) => (
  <svg
    viewBox='0 0 180 240'
    className='h-60 w-auto shrink-0 rounded-xl border'
    role='img'
    aria-label='Anteprima del campo'
  >
    <rect width='180' height='240' fill={colors.secondary_color} />
    <rect width='180' height='36' fill={colors.secondary_color} />
    <rect width='180' height='36' fill='#000' opacity='0.45' />
    <rect y='29' width='180' height='1.5' fill='#fff' opacity='0.9' />
    {logo ? (
      <image href={logo} x='55' y='5' width='70' height='20' />
    ) : (
      <text
        x='90'
        y='20'
        textAnchor='middle'
        fontSize='10'
        fontWeight='700'
        fill={colors.primary_color}
      >
        TennisTalker
      </text>
    )}
    <ellipse cx='156' cy='22' rx='11' ry='5' fill={colors.accent_color} />
    <ellipse cx='156' cy='22' rx='5' ry='2.5' fill='#fff' />
    <g stroke='#fff' strokeOpacity='0.8' strokeWidth='1' fill='none'>
      <line x1='8' y1='36' x2='8' y2='228' />
      <line x1='172' y1='36' x2='172' y2='228' />
      <line x1='8' y1='228' x2='172' y2='228' />
      <line x1='26' y1='136' x2='154' y2='136' />
      <line x1='90' y1='36' x2='90' y2='136' />
    </g>
    <circle cx='70' cy='120' r='5' fill='#d8ff53' />
    <ellipse
      cx='90'
      cy='210'
      rx='24'
      ry='6'
      fill='none'
      stroke={colors.primary_color}
      strokeWidth='2'
    />
  </svg>
)

interface LiveEventFormProps {
  event?: LiveEventAdmin | null
  saving: boolean
  onSubmit: (input: LiveEventInput) => void
}

/**
 * A branded event changes at most the logo and three colours: everything
 * else is TennisTalker by default (the backend applies the same defaults).
 */
const LiveEventForm = ({ event, saving, onSubmit }: LiveEventFormProps) => {
  const [name, setName] = useState(event?.name ?? '')
  const [slug, setSlug] = useState(event?.slug ?? '')
  const [brandName, setBrandName] = useState(event?.brand_name ?? '')
  const [description, setDescription] = useState(event?.description ?? '')
  const [active, setActive] = useState(event?.active ?? false)
  const [startsAt, setStartsAt] = useState(toDateTimeLocal(event?.starts_at))
  const [endsAt, setEndsAt] = useState(toDateTimeLocal(event?.ends_at))
  const [liveKey, setLiveKey] = useState(event?.live_key ?? '')
  const [callTimeout, setCallTimeout] = useState(
    String(event?.queue_call_timeout_seconds ?? 30)
  )
  const [theme, setTheme] = useState(stringValues(event?.raw_theme))
  const [config, setConfig] = useState(stringValues(event?.raw_game_config))
  const [logo, setLogo] = useState<string | null>(null)
  const [removeLogo, setRemoveLogo] = useState(false)

  const colors = Object.fromEntries(
    COLOR_FIELDS.map(({ key }) => [
      key,
      HEX.test(theme[key] ?? '') ? theme[key] : DEFAULT_THEME[key],
    ])
  ) as Record<ColorKey, string>
  const currentLogo = removeLogo ? null : (logo ?? event?.logo ?? null)

  const handleLogo = async (file: File | undefined) => {
    if (!file) return
    setLogo(await readAsDataUrl(file))
    setRemoveLogo(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const gameConfig = Object.fromEntries(
      Object.entries(config)
        .filter(([, v]) => v.trim() !== '' && Number.isFinite(Number(v)))
        .map(([k, v]) => [k, Number(v)])
    )
    const themeValues = Object.fromEntries(
      Object.entries(theme).filter(([, v]) => v.trim() !== '')
    )
    onSubmit({
      name,
      slug,
      brand_name: brandName || null,
      description: description || null,
      active,
      starts_at: fromDateTimeLocal(startsAt),
      ends_at: fromDateTimeLocal(endsAt),
      live_key: liveKey || null,
      queue_call_timeout_seconds: Number(callTimeout) || 30,
      game_type: 'wall_rally',
      game_config: gameConfig,
      theme: themeValues,
      ...(logo && { logo }),
      ...(removeLogo && { remove_logo: true }),
    })
  }

  const setThemeValue = (key: string, value: string) =>
    setTheme((prev) => ({ ...prev, [key]: value }))

  return (
    <form onSubmit={handleSubmit} className='flex max-w-4xl flex-col gap-6'>
      <Card>
        <CardHeader>
          <CardTitle>Evento</CardTitle>
        </CardHeader>
        <CardContent className='grid gap-4 md:grid-cols-2'>
          <div className='flex flex-col gap-2'>
            <Label htmlFor='le-name'>Nome</Label>
            <Input
              id='le-name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className='flex flex-col gap-2'>
            <Label htmlFor='le-slug'>Slug (URL /evento/…)</Label>
            <Input
              id='le-slug'
              value={slug}
              pattern='[a-z0-9-]+'
              title='Solo lettere minuscole, numeri e trattini'
              onChange={(e) => setSlug(e.target.value)}
              required
            />
          </div>
          <div className='flex flex-col gap-2'>
            <Label htmlFor='le-start'>Inizio (vuoto = subito)</Label>
            <Input
              id='le-start'
              type='datetime-local'
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </div>
          <div className='flex flex-col gap-2'>
            <Label htmlFor='le-end'>Fine (vuoto = senza scadenza)</Label>
            <Input
              id='le-end'
              type='datetime-local'
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </div>
          <div className='flex flex-col gap-2'>
            <Label htmlFor='le-key'>Chiave QR (vuoto = nessuna)</Label>
            <div className='flex gap-2'>
              <Input
                id='le-key'
                value={liveKey}
                onChange={(e) => setLiveKey(e.target.value)}
              />
              <Button
                type='button'
                variant='outline'
                onClick={() => setLiveKey(randomKey())}
              >
                Genera
              </Button>
            </div>
            <p className='text-xs text-muted-foreground'>
              Cambiarla invalida i QR già stampati.
            </p>
          </div>
          <div className='flex flex-col gap-2'>
            <Label htmlFor='le-timeout'>
              Secondi per iniziare quando tocca al giocatore
            </Label>
            <Input
              id='le-timeout'
              type='number'
              min={10}
              value={callTimeout}
              onChange={(e) => setCallTimeout(e.target.value)}
            />
          </div>
          <div className='flex items-center gap-3'>
            <Switch
              id='le-active'
              checked={active}
              onCheckedChange={setActive}
            />
            <Label htmlFor='le-active'>Attivo (visibile agli utenti)</Label>
          </div>
          <div className='flex flex-col gap-2 md:col-span-2'>
            <Label htmlFor='le-desc'>Note interne</Label>
            <Textarea
              id='le-desc'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Brand</CardTitle>
          <p className='text-sm text-muted-foreground'>
            Di default è tutto TennisTalker. Per un brand bastano logo e, se
            serve, i tre colori: il resto (testi sui colori, muro, linee,
            pallina) si adatta da solo.
          </p>
        </CardHeader>
        <CardContent className='flex flex-col gap-6 md:flex-row'>
          <CourtPreview colors={colors} logo={currentLogo} />
          <div className='grid flex-1 gap-4'>
            <div className='grid gap-4 md:grid-cols-2'>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='le-brand'>Nome del brand</Label>
                <Input
                  id='le-brand'
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='le-logo'>Logo (sul muro e accanto a TT)</Label>
                <Input
                  id='le-logo'
                  type='file'
                  accept='image/*'
                  onChange={(e) => handleLogo(e.target.files?.[0])}
                />
                {event?.logo && (
                  <label className='flex items-center gap-2 text-sm'>
                    <Checkbox
                      checked={removeLogo}
                      onCheckedChange={(checked) =>
                        setRemoveLogo(checked === true)
                      }
                    />
                    Rimuovi logo (torna il marchio TennisTalker)
                  </label>
                )}
              </div>
            </div>
            <div className='grid gap-4 md:grid-cols-3'>
              {COLOR_FIELDS.map(({ key, label, hint }) => (
                <div key={key} className='flex flex-col gap-2'>
                  <Label htmlFor={`le-${key}`}>{label}</Label>
                  <div className='flex gap-2'>
                    <input
                      type='color'
                      aria-label={label}
                      className='h-9 w-10 shrink-0 cursor-pointer rounded border'
                      value={colors[key]}
                      onChange={(e) => setThemeValue(key, e.target.value)}
                    />
                    <Input
                      id={`le-${key}`}
                      placeholder={DEFAULT_THEME[key]}
                      value={theme[key] ?? ''}
                      onChange={(e) => setThemeValue(key, e.target.value)}
                    />
                  </div>
                  <span className='text-xs text-muted-foreground'>{hint}</span>
                </div>
              ))}
            </div>
            <div className='grid gap-4 md:grid-cols-2'>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='le-title'>Titolo</Label>
                <Input
                  id='le-title'
                  placeholder='Default: nome evento'
                  value={theme.title ?? ''}
                  onChange={(e) => setThemeValue('title', e.target.value)}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='le-subtitle'>Sottotitolo</Label>
                <Input
                  id='le-subtitle'
                  placeholder='Rimanda la palla contro il muro…'
                  value={theme.subtitle ?? ''}
                  onChange={(e) => setThemeValue('subtitle', e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <details className='rounded-xl border bg-card p-6'>
        <summary className='cursor-pointer font-semibold'>
          Difficoltà del gioco (avanzate)
        </summary>
        <p className='mt-2 text-sm text-muted-foreground'>
          Lascia vuoto per usare i valori di default (in grigio).
        </p>
        <div className='mt-4 grid gap-4 md:grid-cols-4'>
          {CONFIG_FIELDS.map(({ key, label, step }) => (
            <div key={key} className='flex flex-col gap-2'>
              <Label htmlFor={`le-${key}`}>{label}</Label>
              <Input
                id={`le-${key}`}
                type='number'
                step={step}
                min={0}
                placeholder={event ? String(event.game_config[key]) : 'default'}
                value={config[key] ?? ''}
                onChange={(e) =>
                  setConfig((prev) => ({ ...prev, [key]: e.target.value }))
                }
              />
            </div>
          ))}
        </div>
      </details>

      <div>
        <Button type='submit' disabled={saving || !name || !slug}>
          {saving && <Loader className='mr-2 h-4 w-4 animate-spin' />}
          Salva
        </Button>
      </div>
    </form>
  )
}

export default LiveEventForm
